import { number, object, string } from 'yup';

import { t } from 'utils/getTranslations';

type ValidationResponse = true | string;

export const isValidNumber = (errorMsg: string): ((input: unknown) => Promise<ValidationResponse>) => {
    return async (input: unknown) => {
        if (input === null || input === undefined || input === '') {
            return true;
        }
        const normalized = typeof input === 'string' ? input.replace(/,/g, '.') : input;
        return Number.isFinite(Number(normalized)) || errorMsg;
    };
};

export const numberIsGreaterThan = (
    moreThan: number,
    errorMsg: string
): ((input: unknown) => Promise<ValidationResponse>) => {
    return async (input: unknown) => {
        const validator = number().moreThan(moreThan);

        try {
            await validator.validate(input);
            return true;
        } catch {
            return errorMsg;
        }
    };
};

export const mustNotExceed = (limit: number, errorMsg: string): ((input: unknown) => Promise<ValidationResponse>) => {
    return async (input: unknown) => {
        // An absent value exceeds nothing — emptiness is a separate rule's business (see isValidNumber).
        if (input === null || input === undefined || input === '') {
            return true;
        }
        const validator = number().max(limit);

        try {
            await validator.validate(input);
            return true;
        } catch {
            return errorMsg;
        }
    };
};

export const isValidUrl = async (input: unknown, errorMsg: string): Promise<ValidationResponse> => {
    const validator = object({
        input: string().url(),
    });

    try {
        const sanitized = typeof input === 'string' ? input.replace(/ /g, '%20') : input;
        await validator.validate({ input: sanitized });
        return true;
    } catch {
        return errorMsg;
    }
};

export const isValidIban = async (input: unknown, errorMsg: string): Promise<ValidationResponse> => {
    const validator = string()
        .min(15)
        .max(34)
        .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/);

    try {
        await validator.validate(input);
        return true;
    } catch {
        return errorMsg;
    }
};

export const isValidSwift = async (input: unknown, errorMsg: string): Promise<ValidationResponse> => {
    const validator = string()
        .min(8)
        .max(11)
        .matches(/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/);

    try {
        await validator.validate(input);
        return true;
    } catch {
        return errorMsg;
    }
};

export const stringIsNotWhitespaceOnly = (errorMsg: string): ((input: unknown) => Promise<ValidationResponse>) => {
    return async (input: unknown) => {
        // Preserve original behavior: only flag invalid when input is a string that trims to empty.
        // Non-strings (null, undefined, numbers, etc.) are treated as valid.
        const isValid = typeof input === 'string' ? input.trim() !== '' : true;
        return isValid || errorMsg;
    };
};

export const isValidEmail = async (input: unknown, errorMsg: string): Promise<ValidationResponse> => {
    const validator = object({
        input: string().email(),
    });

    try {
        await validator.validate({ input });
        return true;
    } catch {
        return errorMsg;
    }
};

export const min = async (input: unknown, min: number, errorMsg?: string): Promise<ValidationResponse> => {
    const validator = object({
        input: string().min(min),
    });

    try {
        await validator.validate({ input });
        return true;
    } catch {
        return errorMsg || t('validation.must-be-atleast-characters-long', { minLength: String(min) });
    }
};

const emailValidator = object({
    input: string().email(),
});

export const isValidEmailList = (errorMsg: string): ((input: unknown) => Promise<ValidationResponse>) => {
    return async (input: unknown) => {
        if (typeof input !== 'string') {
            return true; // null/undefined/non-string is allowed
        }
        if (input.trim() === '') {
            return true; // empty is allowed
        }

        const emails = input
            .split(/[,;]/)
            .map((email: string) => email.trim())
            .filter((email: string) => email !== '');

        try {
            await Promise.all(emails.map((email: string) => emailValidator.validate({ input: email })));
        } catch {
            return errorMsg;
        }

        return true;
    };
};
